import { fetchCallsPlusTransactions, type LotLedgerEvent } from "./callsPlus";
import {
  historyStockLegsForTicker,
  isImpliedStockLeg,
  type HistoryRelatedTxn,
} from "./historyRelated";
import { esc, fmtNum, fmtUsd, normalizeTicker } from "./watchlistData";

export type ShareHistoryTransaction = Awaited<ReturnType<typeof fetchCallsPlusTransactions>>[number];

export interface ShareHistoryMeta {
  ticker: string;
  shares: number | null;
  avgCost: number | null;
  lotStartDate: string;
}

interface SharePosition {
  qty: number;
  avg: number;
}

function fmtShareDelta(shares: number): string {
  if (shares > 0) return `+${fmtNum(shares, 0)}`;
  return fmtNum(shares, 0);
}

function shareDeltaClass(shares: number): string {
  if (shares > 0) return "roi-strong";
  if (shares < 0) return "roi-loss";
  return "";
}

function blendBuy(pos: SharePosition, qty: number, price: number): void {
  if (qty <= 0) return;
  const totalCost = pos.qty * pos.avg + qty * price;
  pos.qty += qty;
  pos.avg = pos.qty > 0 ? totalCost / pos.qty : 0;
}

function reduceQty(pos: SharePosition, qty: number): void {
  if (qty <= 0) return;
  pos.qty = Math.max(0, pos.qty - qty);
  if (pos.qty === 0) pos.avg = 0;
}

function shareLegPrice(txn: HistoryRelatedTxn): number {
  const price = Number(txn.avg_price);
  if (Number.isFinite(price) && price > 0) return price;
  const strike = Number(txn.strike);
  if (Number.isFinite(strike) && strike > 0) return strike;
  return 0;
}

function isPutAssignmentLeg(txn: HistoryRelatedTxn, byId: Map<number, HistoryRelatedTxn>): boolean {
  const linkedId = Number(txn.linked_transaction_id);
  if (Number.isFinite(linkedId) && linkedId > 0) {
    const linked = byId.get(linkedId);
    if (linked?.instrument_type === "option" && linked.side === "put") return true;
  }
  if (txn.close_reason === "assigned" && txn.action === "buy") return true;
  if (isImpliedStockLeg(txn) && txn.action === "buy") {
    const optionId = Number(txn.linked_transaction_id);
    const option = Number.isFinite(optionId) ? byId.get(optionId) : undefined;
    if (option?.side === "put") return true;
  }
  return false;
}

function shareLegLabel(txn: HistoryRelatedTxn, byId: Map<number, HistoryRelatedTxn>): string {
  if (txn.action === "sell") return "Stock sell";
  if (isPutAssignmentLeg(txn, byId)) return "Put assignment";
  return "Stock buy";
}

function buildTxnById(txns: readonly HistoryRelatedTxn[]): Map<number, HistoryRelatedTxn> {
  const byId = new Map<number, HistoryRelatedTxn>();
  for (const txn of txns) {
    const id = Number(txn.id);
    if (id) byId.set(id, txn);
  }
  return byId;
}

function buildShareHistoryFromStockLegs(
  ticker: string,
  txns: readonly ShareHistoryTransaction[],
  accountId?: string | null,
): { events: LotLedgerEvent[]; lotStartDate: string } {
  const legs = historyStockLegsForTicker(txns, ticker, accountId);
  const byId = buildTxnById(txns);

  const pos: SharePosition = { qty: 0, avg: 0 };
  const events: LotLedgerEvent[] = [];
  let lotStartDate = "";
  let currentLotStartDate = "";

  for (const txn of legs) {
    const qty = Number(txn.quantity) || 0;
    const price = shareLegPrice(txn);
    if (qty <= 0 || !txn.trade_date || price <= 0) continue;

    const prevQty = pos.qty;
    const label = shareLegLabel(txn, byId);

    if (txn.action === "buy") {
      if (prevQty === 0) {
        lotStartDate = txn.trade_date;
        currentLotStartDate = txn.trade_date;
      }
      blendBuy(pos, qty, price);
      events.push({
        date: txn.trade_date,
        kind: label === "Put assignment" ? "put_assignment" : "buy",
        label,
        shares: qty,
        price,
        netShares: pos.qty,
        avgPrice: pos.avg,
      });
    } else if (txn.action === "sell") {
      reduceQty(pos, qty);
      events.push({
        date: txn.trade_date,
        kind: "stock_sell",
        label,
        shares: -qty,
        price,
        netShares: pos.qty,
        avgPrice: pos.avg,
      });
      if (pos.qty === 0) lotStartDate = "";
    }
  }

  return { events, lotStartDate: currentLotStartDate || lotStartDate };
}

/** Stock buy/sell legs for a ticker (incl. implied assignment legs), newest first. */
export function getShareHistoryEvents(
  ticker: string,
  txns: readonly ShareHistoryTransaction[],
  accountId?: string | null,
): LotLedgerEvent[] {
  return [...buildShareHistoryFromStockLegs(ticker, txns, accountId).events].reverse();
}

export function buildShareHistoryMeta(
  ticker: string,
  txns: readonly ShareHistoryTransaction[],
  shares: number | null,
  avgCost: number | null,
  accountId?: string | null,
): ShareHistoryMeta {
  const { lotStartDate } = buildShareHistoryFromStockLegs(ticker, txns, accountId);
  return {
    ticker: normalizeTicker(ticker),
    shares,
    avgCost,
    lotStartDate,
  };
}

export function renderShareHistoryTable(events: readonly LotLedgerEvent[]): string {
  if (!events.length) {
    return `<p class="muted small sfe-stocks-share-history-empty">No share buys or sells found for this ticker.</p>`;
  }

  const hasSells = events.some((event) => event.shares < 0);
  const rows = events
    .map((event) => {
      const avgAfter = event.netShares > 0 ? fmtUsd(event.avgPrice) : "—";
      return `<tr>
        <td class="sfe-stocks-share-history-date">${esc(event.date)}</td>
        <td class="sfe-stocks-share-history-event">${esc(event.label)}</td>
        <td class="num ${shareDeltaClass(event.shares)}">${fmtShareDelta(event.shares)}</td>
        <td class="num">${fmtUsd(event.price)}</td>
        <td class="num">${avgAfter}</td>
      </tr>`;
    })
    .join("");

  const sellsNote = hasSells
    ? ""
    : `<p class="muted small sfe-stocks-share-history-note">No assigned covered calls yet — stock sells appear here when a call is assigned (same rows as History → Stocks). BTC closes are not stock sales.</p>`;

  return `
    <div class="sfe-stocks-share-history-scroll">
      <table class="sfe-stocks-share-history-table">
        <thead>
          <tr>
            <th class="sfe-stocks-share-history-date">Date</th>
            <th class="sfe-stocks-share-history-event">Event</th>
            <th class="num">Shares</th>
            <th class="num">Price</th>
            <th class="num">Avg after</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>${sellsNote}`;
}

export function renderShareHistoryPanel(
  meta: ShareHistoryMeta,
  events: readonly LotLedgerEvent[],
  loading = false,
): string {
  const lotDate = meta.lotStartDate || "—";
  const sharesLabel = meta.shares != null ? fmtNum(meta.shares, 0) : "—";
  const avgLabel = meta.avgCost != null ? fmtUsd(meta.avgCost) : "—";

  return `
    <div class="sfe-stocks-share-history-panel">
      <div class="sfe-stocks-share-history-head">
        <strong>${esc(meta.ticker)} share history</strong>
        <span>Stock buys and sells · lot since ${esc(lotDate)} · ${sharesLabel} shares · avg ${avgLabel}</span>
      </div>
      ${
        loading
          ? `<p class="muted small sfe-stocks-share-history-empty">Loading transactions…</p>`
          : renderShareHistoryTable(events)
      }
    </div>`;
}
